<script setup>

import Text from "~/components/atoms/Text.vue";
import ProductPrice from "~/components/molecules/ProductPrice.vue";
import Image from "~/components/atoms/Image.vue";
import SideSlider from "~/components/templates/SideSlider.vue";
import {useClientFetch} from "~/composables/useClientFetch";

const props = defineProps({
  activeSelect: {
    required: false
  },
  collection: {
    type: Object,
    required: false
  },
  select_image: {
    type: String,
    required: false
  },
  sku: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['close', 'loaded'])

const NO_IMAGE = '/img/no-image-available.png'

const show = ref(false)
const close = () => {
  show.value = false
  setTimeout(() => {
    emit('close')
  }, 200)
}

const loaded = ref(false)
const headerImageFailed = ref(false)

const headerImageSrc = computed(() => {
  if (headerImageFailed.value || !props.select_image) {
    return NO_IMAGE
  }

  return props.select_image.startsWith('http')
    ? props.select_image
    : `https://hillceramic.se/${props.select_image}`
})

const showRecommended = (product) => {
  const price = Number(product.hc_volume_price)
  const recommended = Number(product.hc_recommended_price)

  if (!recommended || !price || recommended <= price) {
    return false
  }

  return Math.round(100 - (price / recommended) * 100) > 0
}

const onHeaderImageError = () => {
  if (!headerImageFailed.value) {
    headerImageFailed.value = true
  }
}

watch(() => props.select_image, () => {
  headerImageFailed.value = false
})

onMounted(() => {
  setTimeout(() => {
    show.value = true
  }, 200)

  useClientFetch(`/product/${props.sku}/selects`).then(data => {
    emit('loaded', data.selects)
    loaded.value = true
  })
})
</script>

<template>
  <SideSlider class="selects-side" :bottom="true" width="480px" position="right" :active="show"
              @close="close">
    <template v-if="loaded">
      <div class="header no-flex">
        <div class="image">
          <img :src="headerImageSrc" alt="" @error="onHeaderImageError"/>
        </div>
      </div>
      <div>
        <div class="title">
          {{ props.activeSelect.site_name }}
        </div>

        <div class="products">
          <nuxt-link :to="'/shop/'+product.url" class="product full-width row side-container g-8"
                     :class="{active:sku === product.sku}"
                     v-for="product in props.activeSelect.products">
            <div style="width: 76px; max-width: 76px; max-height: 76px; min-height: 76px;">
              <Image :src="product.image"/>
            </div>
            <div>
              <Text v-html="product.product_name"></Text>

              <Text tag="b" class="mini-label">{{ product.name }}</Text>
            </div>
            <div class="space-between" style="text-align: right; align-items: flex-end;">
              <Text class="delivery" v-html="product.delivery_time"></Text>

              <div class="row g-8">
                <ProductPrice :digits="0" :price="product.hc_volume_price"/>
                <ProductPrice v-if="showRecommended(product)" :digits="0" :price="product.hc_recommended_price" type="old"/>
              </div>
            </div>
          </nuxt-link>
        </div>

        <div class="collection no-flex" style="height: 61px; align-items: center; justify-content: center;">
          <nuxt-link :to="'/'+props.collection.slug" class="g-4 row">
            <Text underline>{{ $t('collection.collection') }}
              <b>{{ collection.name }}</b></Text>
            <img src="/images/moves/external-link.png"/>
          </nuxt-link>
        </div>
      </div>
    </template>
    <template v-else>
      <img class="center-auto" style="width: 53px; margin-top: auto; margin-bottom: auto;" src="/gif/preloader.gif"/>
    </template>
  </SideSlider>
</template>

<style scoped lang="scss">
.header {
  max-height: 140px;
  min-height: 140px;
  overflow: hidden;

  @include mobile {
    min-height: 35px;
  }

  .image {
    @include mobile {
      display: none;
    }

    img {
      width: 100%;
      height: 140px;
      object-fit: cover;
      display: block;
    }
  }
}

.selects-side:deep(.header) {
  height: 0;
  padding: 0;

  svg {
    background: $white;
    border-radius: 25px;
    position: absolute;
    width: 30px !important;
    height: 30px !important;
    z-index: 999;
    padding: 8px;
    left: 25px;
    top: 15px;

    @include mobile {
      display: none;
    }
  }
}

.selects:deep(.main-block) {
  overflow: hidden;
}

.title {
  position: absolute;
  top: -15px;
  left: 32px;
  min-width: 93px;
  padding: 0 11px;
  height: 30px;
  background: rgba(34, 34, 34, .9);
  box-shadow: 0 32px 48px rgba(82, 82, 82, .08);
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
}

.side-container {
  padding: 18px 32px;

  @include smd {
    padding: 18px 24px;
  }
}

.products {
  overflow: auto;
  margin-top: 25px;
  max-height: calc(100vh - 220px);

  @include mobile {
    max-height: 65%;
  }

  .product {
    font-size: 12px;
    align-items: stretch;

    &:hover, &.active {
      background: $light-grey;
    }

    &:deep(.price) {
      font-size: 13px;
    }
  }
}

.delivery {
  font-size: 11px;
  color: $light-black;
}

a {
  display: flex;
}

.mini-label {
  background: $black;
  color: $white;
  width: fit-content;
  padding: 0 10px;
  font-size: 14px;
  margin-top: 10px;
}
</style>
